import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { Trophy, Medal, BarChart2, EyeIcon, FileTextIcon, Loader2 } from "lucide-react";
import { getModelDisplayName } from '../constants/modelConstants';

// Types
type Mode = 'text' | 'vision';

interface BenchmarkResultEntry {
  modelId: string,
  textScore?: number,
  visionScore?: number,
}

interface BenchmarkDoc {
  title: string,
  description: string,
  link?: string,
  results: BenchmarkResultEntry[],
}

const BenchmarkDisplay: React.FC = () => {
  const [mode, setMode] = useState<Mode>('text');
  const [loading, setLoading] = useState<boolean>(false);
  const [benchmarks, setBenchmarks] = useState<BenchmarkDoc[]>([]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      const querySnap = await getDocs(collection(db, "benchmarks"));
      const docs: BenchmarkDoc[] = [];
      querySnap.forEach((doc) => { docs.push(doc.data() as BenchmarkDoc); });
      setBenchmarks(docs);
      setLoading(false);
    }
    fetchData();
  }, []);
  
  if (loading) return (
    <div className="flex justify-center mt-8 text-terminal-green font-vt323">
      <Loader2 className="animate-spin mr-2" size={24} />
      Caricamento benchmarks...
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-black/50 border border-terminal-green p-5 rounded">
        <h2 className="font-press-start text-xl text-center mb-6 flex items-center justify-center gap-3">
          <BarChart2 className="text-terminal-green" size={24} />
          BENCHMARK SCACCHI LLM
          <BarChart2 className="text-terminal-green" size={24} />
        </h2>

        {benchmarks.map((benchmark, bidx) => {
          const sorted = [...benchmark.results].sort((a, b) => 
            (mode === 'text'
              ? (b.textScore ?? -999) - (a.textScore ?? -999)
              : (b.visionScore ?? -999) - (a.visionScore ?? -999)
            )
          );
          return (
            <div key={bidx} className="mb-10">
              <h3 className="font-vt323 text-lg text-terminal-green text-center mb-1">{benchmark.title}</h3>
              <p className="font-vt323 text-terminal-green/70 text-center text-sm mb-3">
                {benchmark.description}
                {" "}
                {benchmark.link && (
                  <a href={benchmark.link} target="_blank" rel="noopener noreferrer"
                    className="underline text-terminal-green hover:text-terminal-green/80 transition-colors">
                    dataset
                  </a>
                )}
              </p>
              {/* Switch */}
              <div className="flex justify-center mb-4">
                <label className="inline-flex items-center cursor-pointer">
                  <span className={`mr-2 font-vt323 text-sm ${mode === 'text' ? 'text-terminal-green' : 'text-terminal-green/60'}`}>
                    Testo
                  </span>
                  <input type="checkbox" className="sr-only"
                    checked={mode === 'vision'}
                    onChange={() => setMode(mode === 'text' ? 'vision' : 'text')}
                  />
                  <span className="w-10 h-5 flex items-center bg-black/40 border border-terminal-green rounded-full transition-colors px-1">
                    <span className={`w-4 h-4 rounded-full bg-terminal-green transition-transform duration-150 ${mode === 'vision' ? 'translate-x-5' : ''}`} />
                  </span>
                  <span className={`ml-2 font-vt323 text-sm ${mode === 'vision' ? 'text-terminal-green' : 'text-terminal-green/60'}`}>
                    Visione
                  </span>
                </label>
              </div>

              {/* Tabella */}
              <div className="font-vt323">
                <div className="grid grid-cols-8 gap-2 p-2 border-b border-terminal-green/30 mb-3 text-lg">
                  <div className="col-span-1 font-bold">#</div>
                  <div className="col-span-3 font-bold">MODELLO</div>
                  <div className="col-span-4 font-bold text-right">
                    {mode === 'text' ? 'PUNTEGGIO TESTO' : 'PUNTEGGIO VISIONE'}
                  </div>
                </div>
                {sorted.map((benchmark, index) => {
                  const score = mode === 'text' ? benchmark.textScore : benchmark.visionScore;
                  if (score === undefined) return null;
                  return (
                    <div key={index}
                      className={`grid grid-cols-8 gap-2 p-2 ${index % 2 === 0 ? 'bg-black/30' : ''} hover:bg-terminal-green/20 transition-colors text-lg`}>
                      <div className="col-span-1 flex items-center">
                        {index === 0 ? (
                          <Trophy size={18} className="text-yellow-500" />
                        ) : index === 1 ? (
                          <Medal size={18} className="text-gray-300" />
                        ) : index === 2 ? (
                          <Medal size={18} className="text-amber-600" />
                        ) : (
                          <span>{index + 1}</span>
                        )}
                      </div>
                      <div className="col-span-3 truncate">{benchmark.modelId}</div>
                      <div className="col-span-4 text-right font-bold text-yellow-500">{score}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        <div className="mt-6 p-4 border border-terminal-green/30 bg-black/30 font-vt323">
          <h3 className="text-lg mb-2 text-terminal-green">Informazioni sui Benchmark:</h3>
          <ul className="list-disc list-inside space-y-1 text-terminal-green/70">
            <li>I test sono basati su set di puzzle da Lichess di varia difficoltà</li>
            <li>Modalità Testo: ai modelli viene fornita la notazione FEN e l'ultima mossa</li>
            <li>Modalità Visione: ai modelli viene fornita un'immagine della scacchiera con l'ultima mossa evidenziata</li>
            <li>I punteggi sono calcolati utilizzando il sistema di rating Elo basato sulla difficoltà del puzzle</li>
            <li>
              Scopri il tuo punteggio giocando i{' '}
              <a href="https://lichess.org/training" target="_blank" className="text-terminal-green hover:underline">
                Puzzle di Lichess
              </a>{' '}
              (account richiesto)
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BenchmarkDisplay;